/**
 * Toast Container
 * Toast 메시지를 화면에 표시하는 컴포넌트
 */
import React from 'react';
import { useToast } from './ToastContext';
import { ToastMessage, ToastType, ToastPosition } from './Toast.types';

/**
 * Toast 아이템 컴포넌트
 */
const ToastItem: React.FC<{
    toast: ToastMessage;
    onClose: (id: string) => void;
}> = ({ toast, onClose }) => {
    const getBackgroundColor = (type: ToastType): string => {
        switch (type) {
            case ToastType.SUCCESS:
                return '#28a745';
            case ToastType.ERROR:
                return '#dc3545';
            case ToastType.WARNING:
                return '#ffc107';
            case ToastType.INFO:
                return '#17a2b8';
            default:
                return '#6c757d';
        }
    };

    return (
        <div
            style={{
                backgroundColor: getBackgroundColor(toast.type),
                color: '#fff',
                padding: '12px 20px',
                borderRadius: '4px',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minWidth: '250px',
                maxWidth: '500px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                animation: 'slideIn 0.3s ease-out',
            }}
        >
            <span>{toast.message}</span>
            <button
                onClick={() => onClose(toast.id)}
                style={{
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    fontSize: '20px',
                    cursor: 'pointer',
                    marginLeft: '10px',
                    padding: '0 5px',
                }}
            >
                ×
            </button>
        </div>
    );
};

/**
 * Toast Container
 */
export const ToastContainer: React.FC = () => {
    const { toasts, hideToast } = useToast();

    // 위치별로 Toast 그룹화
    const groupedToasts = toasts.reduce(
        (acc, toast) => {
            const position = toast.position || ToastPosition.TOP_CENTER;
            if (!acc[position]) {
                acc[position] = [];
            }
            acc[position].push(toast);
            return acc;
        },
        {} as Record<ToastPosition, ToastMessage[]>
    );

    const getPositionStyle = (position: ToastPosition): React.CSSProperties => {
        const baseStyle: React.CSSProperties = {
            position: 'fixed',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
        };

        switch (position) {
            case ToastPosition.TOP_LEFT:
                return { ...baseStyle, top: '20px', left: '20px' };
            case ToastPosition.TOP_CENTER:
                return { ...baseStyle, top: '20px', left: '50%', transform: 'translateX(-50%)' };
            case ToastPosition.TOP_RIGHT:
                return { ...baseStyle, top: '20px', right: '20px' };
            case ToastPosition.BOTTOM_LEFT:
                return { ...baseStyle, bottom: '20px', left: '20px' };
            case ToastPosition.BOTTOM_CENTER:
                return { ...baseStyle, bottom: '20px', left: '50%', transform: 'translateX(-50%)' };
            case ToastPosition.BOTTOM_RIGHT:
                return { ...baseStyle, bottom: '20px', right: '20px' };
            default:
                return { ...baseStyle, top: '20px', left: '50%', transform: 'translateX(-50%)' };
        }
    };

    return (
        <>
            <style>
                {`
                    @keyframes slideIn {
                        from {
                            opacity: 0;
                            transform: translateY(-20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                `}
            </style>
            {Object.entries(groupedToasts).map(([position, positionToasts]) => (
                <div key={position} style={getPositionStyle(position as ToastPosition)}>
                    {positionToasts.map((toast) => (
                        <ToastItem key={toast.id} toast={toast} onClose={hideToast} />
                    ))}
                </div>
            ))}
        </>
    );
};
